import * as colorPicker from '@zag-js/color-picker';
import { useRef, useState } from 'react';

interface DraftState {
  color: colorPicker.Color;
  propKey: string;
}

function asHsba(color: colorPicker.Color): colorPicker.Color {
  return color.toFormat('hsba');
}

function colorKey(color: colorPicker.Color): string {
  return color.toFormat('rgba').toString('rgba');
}

/** Maintains an HSBA draft while selectively adopting controlled prop updates. */
export function useControlledColorDraft(color: colorPicker.Color, shouldDeferExternalUpdates: () => boolean) {
  const [state, setState] = useState<DraftState>(() => ({
    color: asHsba(color),
    propKey: colorKey(color),
  }));
  const currentRef = useRef(state.color);

  const propKey = colorKey(color);
  let draft = state.color;
  if (state.propKey !== propKey) {
    draft = shouldDeferExternalUpdates() ? state.color : asHsba(color);
    currentRef.current = draft;
    setState({ color: draft, propKey });
  }

  function getCurrent(): colorPicker.Color {
    return currentRef.current;
  }

  function update(nextColor: colorPicker.Color): colorPicker.Color {
    const hsba = asHsba(nextColor);
    currentRef.current = hsba;
    setState((current) => ({ ...current, color: hsba }));
    return hsba;
  }

  return { draft, getCurrent, update };
}
